"use client";

// This component sits at the junction of auth state, socket.io status
// pushes, and a UI that mirrors a remote system. Its effects exist *to*
// call setState on mount / on subscription events / on auth transitions,
// which is exactly what the react-hooks/set-state-in-effect rule flags.
// The rule is right on average but wrong for this file's job; disable it
// file-wide rather than sprinkle per-line waivers.
/* eslint-disable react-hooks/set-state-in-effect */

import { useEffect, useState } from "react";
import { ExternalLink, Headphones, Loader2, LogIn, LogOut, Play, Radio, SatelliteDish, StopCircle, X } from "lucide-react";

import { Button } from "@/components/ui/button";
import { clearLoggedInFlagCookieLocally, hasLoggedInFlagCookie } from "@/lib/auth/client-flag";
import { thatradiothingApiUrl } from "@/lib/thatradiothing";
import { useThatRadioThingSocket } from "@/lib/thatradiothing/socket-context";
import { useThatradiothingOnline } from "@/lib/use-thatradiothing-online";
import { cn } from "@/lib/utils";

type ThatRadioThingClientProps = {
  mode: "home" | "floating";
  title: string;
  subtitle: string;
  openLabel: string;
  hideLabel?: string;
  onHide?: () => void;
};

const PLAYER_URL = thatradiothingApiUrl("player");

type SpotifyImage = {
  url?: string;
};

type SpotifyArtist = {
  name?: string;
  external_urls?: { spotify?: string };
};

type SpotifyTrack = {
  name?: string;
  external_urls?: { spotify?: string };
  artists?: SpotifyArtist[];
  album?: {
    name?: string;
    images?: SpotifyImage[];
    external_urls?: { spotify?: string };
  };
};

type RadioUserSummary = {
  session_id?: string;
  enabled?: boolean;
  spotify_profile?: {
    display_name?: string;
  } | null;
};

type RadioStatus = {
  now_playing: SpotifyTrack | null;
  master_user?: {
    display_name?: string | null;
    external_url?: string | null;
  };
  listeners?: number;
  enabled?: boolean;
  profile?: RadioProfile["profile"];
  can_be_master?: boolean;
  is_master?: boolean;
  message?: string;
  devices?: { devices?: RadioDevice[] } | RadioDevice[];
  users?: RadioUserSummary[];
};

type RadioProfile = {
  profile?: {
    display_name?: string;
  };
  can_be_master?: boolean;
  is_master?: boolean;
  message?: string;
};

type RadioDevice = {
  id: string;
  name: string;
  is_active: boolean;
  selected_device: boolean;
};

type AuthMeResponse = {
  authenticated: boolean;
  user?: {
    displayName?: string | null;
  };
};

async function fetchJson<T>(url: string, init?: RequestInit): Promise<{ ok: boolean; status: number; data: T | null }> {
  try {
    const response = await fetch(url, {
      ...init,
      cache: "no-store",
      credentials: "include",
    });

    if (response.status === 204) {
      return { ok: response.ok, status: response.status, data: null };
    }

    const text = await response.text();
    const data = text ? (JSON.parse(text) as T) : null;
    return { ok: response.ok, status: response.status, data };
  } catch {
    return { ok: false, status: 0, data: null };
  }
}

export function ThatRadioThingClient({
  mode,
  title,
  subtitle,
  openLabel,
  hideLabel = "Hide radio client",
  onHide,
}: ThatRadioThingClientProps) {
  const isFloating = mode === "floating";
  const online = useThatradiothingOnline();
  const [authLoading, setAuthLoading] = useState(true);
  const [authenticated, setAuthenticated] = useState(false);
  const [authDisplayName, setAuthDisplayName] = useState<string | null>(null);
  const [status, setStatus] = useState<RadioStatus | null>(null);
  const [profile, setProfile] = useState<RadioProfile | null>(null);
  const [devices, setDevices] = useState<RadioDevice[]>([]);
  const [busy, setBusy] = useState(false);
  const [showDeviceMenu, setShowDeviceMenu] = useState(false);

  const nowPlaying = status?.now_playing ?? null;
  const albumImageUrl = nowPlaying?.album?.images?.[0]?.url ?? null;
  const selectedDevice = devices.find((device) => device.selected_device) ?? null;

  async function loadAuth() {
    // Local-truth gate: the flag cookie tells us synchronously whether
    // there's any session at all on .duudey.com (set in lockstep with
    // the HttpOnly JWT by both the site and thatradiothing). For
    // guests, skip the /api/auth/me probe entirely — the radio dock
    // doesn't need a user object, just the right polling target.
    if (!hasLoggedInFlagCookie()) {
      setAuthenticated(false);
      setAuthDisplayName(null);
      setAuthLoading(false);
      return;
    }

    setAuthLoading(true);
    const response = await fetchJson<AuthMeResponse>("/api/auth/me");
    if (!response.ok || !response.data?.authenticated) {
      // Server disagrees with the flag — most likely a stale flag
      // after secret rotation. Drop it locally so subsequent polls
      // take the guest path; if the JWT actually still validates
      // the next probe will re-mint the flag.
      clearLoggedInFlagCookieLocally();
      setAuthenticated(false);
      setAuthDisplayName(null);
      setAuthLoading(false);
      return;
    }

    setAuthenticated(true);
    setAuthDisplayName(response.data.user?.displayName ?? null);
    setAuthLoading(false);
  }

  async function loadStatus() {
    // Logged-in users get their full status (now-playing, profile,
    // devices, master flags) via the shared Socket.IO connection in
    // ``ThatRadioThingSocketProvider`` — the server replays the current
    // state on every connect, so we no longer need a REST round-trip
    // here. The 401 self-heal that previously lived in this branch is
    // covered by ``loadAuth()`` (which probes ``/api/auth/me`` and
    // clears the flag if the session is stale) and by the socket's own
    // failed-handshake path.
    //
    // Guests still need a now-playing read because the socket isn't
    // opened for them (they cannot be the master) — the public
    // ``/api/now_playing`` endpoint returns just the current track.
    if (hasLoggedInFlagCookie()) {
      return;
    }

    const response = await fetchJson<{ now_playing: SpotifyTrack | null }>(thatradiothingApiUrl("api/now_playing"));
    if (response.ok) {
      setStatus({ now_playing: response.data?.now_playing ?? null });
    }
  }

  async function callRadioAction(path: string, init?: RequestInit) {
    setBusy(true);
    await fetchJson(path, init ?? { method: "POST" });
    await loadStatus();
    setBusy(false);
  }

  function startSpotifyLogin() {
    if (typeof window === "undefined") return;
    const returnTo = `${window.location.pathname}${window.location.search}`;
    const origin = window.location.origin;
    window.location.href = `/api/auth/spotify/login?returnTo=${encodeURIComponent(returnTo)}&origin=${encodeURIComponent(origin)}`;
  }

  async function logout() {
    await fetchJson("/api/auth/logout", { method: "POST" });
    setAuthenticated(false);
    setAuthDisplayName(null);
    setProfile(null);
    setDevices([]);
    setShowDeviceMenu(false);
    setStatus(null);
    await loadStatus();
  }

  useEffect(() => {
    // On-mount data fetch; the setState calls inside loadAuth are the whole
    // point of this effect.
    void loadAuth();
  }, []);

  useEffect(() => {
    if (!authenticated) {
      setShowDeviceMenu(false);
    }
  }, [authenticated]);

  // Layout-scoped socket: ``ThatRadioThingSocketProvider`` (mounted in
  // ``[lang]/layout.tsx``) owns the single Socket.IO connection for the
  // tab. We subscribe via context and mirror the latest payload into our
  // local state shape (``status`` + ``profile`` + ``devices``).
  //
  // ``loadStatus()`` is still called on auth transitions so we get an
  // immediate REST snapshot before the socket replays its first
  // ``status`` event — and so the 401 self-heal path (clearing the flag
  // cookie when thatradiothing rejects a stale session) keeps running on
  // a proper HTTP response code.
  const { status: sharedRadioStatus } = useThatRadioThingSocket();

  useEffect(() => {
    if (!authenticated) {
      setProfile(null);
      setDevices([]);
      void loadStatus();
      return;
    }

    void loadStatus();
  }, [authenticated]);

  // Mirror the shared socket payload into the local component state. The
  // provider only emits when the server pushes a ``status`` event, so
  // this stays inert for guests (``sharedRadioStatus`` stays ``null``)
  // and during disconnects.
  useEffect(() => {
    if (!authenticated) return;
    if (!sharedRadioStatus) return;
    const data = sharedRadioStatus as RadioStatus;
    setStatus(data);
    setProfile({
      profile: data.profile,
      can_be_master: data.can_be_master,
      is_master: data.is_master,
      message: data.message,
    });
    const deviceList = Array.isArray(data.devices)
      ? data.devices
      : data.devices?.devices ?? [];
    setDevices(deviceList);
  }, [authenticated, sharedRadioStatus]);

  // Hide the whole module (both home card and floating widget) when the
  // probe says thatradiothing is unreachable. Optimistic `null` state keeps
  // the UI rendered while the first probe is in flight.
  if (online === false) {
    return null;
  }

  return (
    <section
      className={cn(
        "overflow-hidden rounded-2xl border border-border bg-card/80 backdrop-blur-sm",
        isFloating ? "w-[min(26rem,calc(100vw-2rem))] shadow-2xl" : "min-w-0",
      )}
    >
      <header className="flex items-center justify-between border-b border-border px-4 py-2.5">
        <div className="min-w-0">
          <p className="truncate text-[11px] uppercase tracking-[0.14em] text-rose-700 dark:text-rose-200">{title}</p>
          <p className="truncate text-xs text-muted-foreground">{subtitle}</p>
        </div>
        <div className="flex items-center gap-1.5">
          <a
            href={PLAYER_URL}
            target="_blank"
            rel="noreferrer"
            title={openLabel}
            aria-label={openLabel}
            className="inline-flex items-center gap-1 rounded-lg border border-border bg-muted px-2 py-1 text-xs text-foreground transition hover:bg-accent cursor-pointer"
          >
            <Radio className="size-3.5" />
            <ExternalLink className="size-3.5" />
          </a>

        {isFloating && onHide ? (
            <Button
              type="button"
              onClick={onHide}
              variant="outline"
              size="icon-xs"
              title={hideLabel}
              aria-label={hideLabel}
              className="rounded-lg border-border bg-muted text-foreground hover:bg-accent cursor-pointer"
            >
              <X className="size-3.5" />
            </Button>
          ) : null}
        </div>
      </header>

      <div className={cn("space-y-3 p-3", isFloating ? "max-h-[24rem] overflow-auto" : "overflow-auto")}>
        {authLoading ? (
          <div className="flex items-center gap-2 rounded-xl border border-border bg-muted px-3 py-2 text-xs text-foreground">
            <Loader2 className="size-4 animate-spin" />
            Checking session...
          </div>
        ) : null}

        {!authLoading && !authenticated ? (
          <div className="space-y-3 rounded-xl border border-border bg-muted p-3 text-xs text-foreground">
            <p>Login with Spotify to listen.</p>
            <Button
              type="button"
              onClick={startSpotifyLogin}
              size="sm"
              className="rounded-xl bg-emerald-300 text-zinc-900 hover:bg-emerald-200"
            >
              <LogIn className="mr-2 size-4" />
              Login with Spotify
            </Button>
          </div>
        ) : null}

        {authenticated ? (
          <div className="space-y-3">
            {!isFloating ? (
              <div className="flex items-center justify-between gap-2 rounded-xl border border-border bg-muted px-3 py-2 text-xs text-foreground">
                <span className="truncate">Logged in as {authDisplayName ?? profile?.profile?.display_name ?? "Spotify user"}</span>
                <Button
                  type="button"
                  size="xs"
                  variant="outline"
                  className="rounded-lg border-border bg-muted text-foreground hover:bg-accent"
                  onClick={() => void logout()}
                >
                  <LogOut className="mr-1.5 size-3.5" />
                  Logout
                </Button>
              </div>
            ) : null}

            {!isFloating && profile?.message ? (
              <div className="rounded-xl border border-amber-400/30 bg-amber-400/5 px-3 py-2 text-xs text-amber-700 dark:text-amber-100">
                {profile.message}
              </div>
            ) : null}

            <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
              {status?.master_user?.display_name ? (
                <span className="inline-flex items-center gap-1 rounded-full border border-border bg-muted px-2 py-1">
                  <Radio className="size-3" />
                  {status.master_user.display_name}
                </span>
              ) : null}
              {typeof status?.listeners === "number" ? (
                <span className="group relative inline-flex items-center gap-1 rounded-full border border-border bg-muted px-2 py-1">
                  <Headphones className="size-3" />
                  {status.listeners}
                  {profile?.can_be_master && status.users && status.users.length > 0 ? (
                    <span className="pointer-events-none absolute left-0 top-full z-20 mt-1 hidden min-w-[10rem] rounded-md border border-border bg-popover px-2 py-1.5 text-[11px] leading-5 text-foreground shadow-lg group-hover:block">
                      {status.users.map((u) => (
                        <span key={u.session_id} className="block truncate">
                          {u.spotify_profile?.display_name ?? "(unknown)"}
                          {u.enabled === false ? <span className="opacity-60"> · off</span> : null}
                        </span>
                      ))}
                    </span>
                  ) : null}
                </span>
              ) : null}
            </div>

            {!isFloating ? (
              <>
                <div className="grid grid-cols-2 gap-2">
                  <Button
                    type="button"
                    size="sm"
                    disabled={busy}
                    onClick={() => void callRadioAction(status?.enabled ? thatradiothingApiUrl("disable") : thatradiothingApiUrl("enable"))}
                    className="rounded-xl bg-white text-zinc-900 hover:bg-zinc-200 cursor-pointer"
                  >
                    {status?.enabled ? <StopCircle className="mr-2 size-4" /> : <Play className="mr-2 size-4" />}
                    {status?.enabled ? "Stop" : "Play"}
                  </Button>

                  {profile?.can_be_master ? (
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      disabled={busy}
                      onClick={() => void callRadioAction(profile?.is_master ? thatradiothingApiUrl("resign") : thatradiothingApiUrl("master"))}
                      className="rounded-xl border-border bg-muted text-foreground hover:bg-accent cursor-pointer"
                    >
                      <SatelliteDish className={cn("mr-2 size-4", profile?.is_master ? "text-red-600 dark:text-red-400" : "text-foreground")} />
                      {profile?.is_master ? "Resign" : "Go Master"}
                    </Button>
                  ) : (
                    <div className="rounded-xl border border-border bg-muted px-3 py-2 text-xs text-muted-foreground">Master access not available</div>
                  )}
                </div>

                <div className="space-y-2 rounded-xl border border-border bg-muted p-3">
                  <p className="text-[11px] uppercase tracking-[0.12em] text-muted-foreground">Devices</p>
                  {devices.length === 0 ? <p className="text-xs text-muted-foreground">No devices found</p> : null}
                  {devices.map((device) => (
                    <div key={device.id} className="flex items-center justify-between gap-2 rounded-lg border border-border bg-card/40 px-2 py-1.5 text-xs text-foreground">
                      <span className="truncate">
                        {device.selected_device ? "[S] " : ""}
                        {device.is_active ? "[A] " : ""}
                        {device.name}
                      </span>
                      {!device.selected_device ? (
                        <Button
                          type="button"
                          size="xs"
                          variant="outline"
                          disabled={busy}
                          className="rounded-lg border-border bg-muted text-foreground hover:bg-accent"
                          onClick={() =>
                            void callRadioAction(thatradiothingApiUrl("devices"), {
                              method: "POST",
                              headers: { "Content-Type": "application/json" },
                              body: JSON.stringify({ device_id: device.id }),
                            })
                          }
                        >
                          Select
                        </Button>
                      ) : null}
                    </div>
                  ))}
                </div>
              </>
            ) : null}
          </div>
        ) : null}

        {nowPlaying ? (
          <article className="overflow-hidden rounded-xl border border-border bg-card/60">
            {isFloating ? (
              <div className="p-2.5">
                <div className="flex items-start gap-2">
                  {albumImageUrl ? (
                    <img
                      src={albumImageUrl}
                      alt={nowPlaying.name ?? "Now playing"}
                      className="size-14 shrink-0 rounded-md object-cover"
                    />
                  ) : null}

                  <div className="min-w-0 flex-1">
                    <p className="text-[11px] uppercase tracking-[0.12em] text-muted-foreground">Now Playing</p>
                    <p className="line-clamp-2 text-sm font-semibold text-foreground">{nowPlaying.name ?? "Unknown track"}</p>
                    {profile?.message ? (
                      <p className="line-clamp-1 text-xs text-amber-700 dark:text-amber-200">{profile.message}</p>
                    ) : (
                      <p className="line-clamp-1 text-xs text-muted-foreground">{nowPlaying.artists?.[0]?.name ?? "Unknown artist"}</p>
                    )}
                  </div>

                  {authenticated ? (
                    <div className="relative shrink-0">
                      <Button
                        type="button"
                        size="sm"
                        disabled={busy}
                        onClick={() => void callRadioAction(status?.enabled ? thatradiothingApiUrl("disable") : thatradiothingApiUrl("enable"))}
                        className="size-10 rounded-xl bg-white p-0 text-zinc-900 hover:bg-zinc-200 cursor-pointer"
                        title={status?.enabled ? "Stop" : "Play"}
                        aria-label={status?.enabled ? "Stop" : "Play"}
                      >
                        {status?.enabled ? <StopCircle className="size-7" /> : <Play className="size-7" />}
                      </Button>

                      {profile?.can_be_master ? (
                        <button
                          type="button"
                          disabled={busy}
                          onClick={() => void callRadioAction(profile?.is_master ? thatradiothingApiUrl("resign") : thatradiothingApiUrl("master"))}
                          title={profile?.is_master ? "Resign master" : "Become master"}
                          aria-label={profile?.is_master ? "Resign master" : "Become master"}
                          className={cn(
                            "absolute -bottom-2 -right-2 inline-flex items-center justify-center rounded-full border bg-popover transition cursor-pointer",
                            profile?.is_master
                              ? "size-6 border-red-400/70 text-red-600 dark:text-red-400"
                              : "size-5 border-white/40 text-foreground"
                          )}
                        >
                          <SatelliteDish className={cn(profile?.is_master ? "size-4" : "size-3")} />
                        </button>
                      ) : null}
                    </div>
                  ) : null}
                </div>

                {authenticated && selectedDevice ? (
                  <div className="mt-2">
                    <Button
                      type="button"
                      size="xs"
                      variant="outline"
                      disabled={busy}
                      onClick={() => setShowDeviceMenu((current) => !current)}
                      className="rounded-lg border-border bg-muted text-foreground hover:bg-accent"
                    >
                      Device: {selectedDevice.name}
                    </Button>

                    {showDeviceMenu ? (
                      <div className="mt-2 space-y-1 rounded-lg border border-border bg-card/70 p-2">
                        {devices.length === 0 ? <p className="text-xs text-muted-foreground">No devices found</p> : null}
                        {devices.map((device) => (
                          <div key={device.id} className="flex items-center justify-between gap-2 rounded-md border border-border bg-card/40 px-2 py-1 text-xs text-foreground">
                            <span className="truncate">
                              {device.selected_device ? "[S] " : ""}
                              {device.is_active ? "[A] " : ""}
                              {device.name}
                            </span>
                            {!device.selected_device ? (
                              <Button
                                type="button"
                                size="xs"
                                variant="outline"
                                disabled={busy}
                                className="rounded-md border-border bg-muted text-foreground hover:bg-accent"
                                onClick={() =>
                                  void callRadioAction(thatradiothingApiUrl("devices"), {
                                    method: "POST",
                                    headers: { "Content-Type": "application/json" },
                                    body: JSON.stringify({ device_id: device.id }),
                                  })
                                }
                              >
                                Select
                              </Button>
                            ) : null}
                          </div>
                        ))}
                      </div>
                    ) : null}
                  </div>
                ) : null}
              </div>
            ) : (
              <>
                {albumImageUrl ? (
                  <img
                    src={albumImageUrl}
                    alt={nowPlaying.name ?? "Now playing"}
                    className="w-full object-cover"
                  />
                ) : null}
                <div className="space-y-1 p-3">
                  <p className="text-xs text-muted-foreground">Now Playing</p>
                  <p className="line-clamp-2 text-sm font-semibold text-foreground">{nowPlaying.name ?? "Unknown track"}</p>
                  <p className="line-clamp-1 text-xs text-muted-foreground">{nowPlaying.artists?.[0]?.name ?? "Unknown artist"}</p>
                </div>
              </>
            )}
          </article>
        ) : null}
      </div>
    </section>
  );
}
